import React, { useState } from 'react'
import { ImportTabsState, ImportTab, ProcoreProject, ImportProjectProps } from './Types/types'
import { ApplicationState } from '../Store/intialState'
import LargeModal from 'UI/LargeModal'
import SyncIcon from '@material-ui/icons/Sync'
import SyncProblemIcon from '@material-ui/icons/SyncProblem'
import useShallowEqualSelector from 'hooks/global'
import {
  Container,
  Title,
  Subtitle,
  StyledTabItem,
  ProjectContainer,
  ProjectText,
  StyledTabs,
  ProjectsSelected,
  StyledTickIcon,
  ProjectWrapper
} from './Styles/importProjects'
import { useDispatch } from 'react-redux'
import { saveProcoreProjects } from './Store/actions'
import SmBannerModal from 'UI/SmBannerModal'
import { editCompany } from 'Companies/Store/actions'
import moment from 'moment'

const ImportProjects: React.FC<ImportProjectProps> = ({ setImportProjects }) => {
  const { company, procoreProjectsList, procoreLoading, error } = useShallowEqualSelector(
    (state: ApplicationState) => ({
      company: state.companies?.company,
      procoreProjectsList: state.projects.procoreProjects,
      error: state.companies.error,
      procoreLoading: state.projects.procoreLoading
    })
  )

  const [tab, setTab] = useState<ImportTabsState>('new')
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([])
  const [modalVisible, setModalVisible] = useState<boolean>(false)

  const dispatch = useDispatch()

  const handleProjectClick = (id: number) => {
    if (selectedProjectIds.includes(id)) {
      const newSelectedProjectIds = selectedProjectIds.filter(project => project !== id)

      return setSelectedProjectIds(newSelectedProjectIds)
    }
    setSelectedProjectIds([...selectedProjectIds, id])
  }

  const handleImportClick = () => {
    setModalVisible(true)

    if (!company.procore || !company.procore.authToken) {
      return
    }

    const procoreProjects = selectedProjectIds.reduce((acc: ProcoreProject[], id) => {
      const project = procoreProjectsList.find(project => project.id === id)

      return project ? [...acc, project] : acc
    }, [])

    dispatch(saveProcoreProjects(procoreProjects, company.procore.authToken))
    dispatch(
      editCompany(company._id, {
        procore: {
          ...company.procore,
          lastImport: moment().toISOString()
        }
      })
    )
  }

  const newProjects = !company.procore?.lastImport
    ? procoreProjectsList
    : procoreProjectsList.filter(project =>
        moment(project.created_at).isAfter(company.procore?.lastImport)
      )

  const tabs: ImportTab[] = [
    { label: 'New', id: 'new' },
    { label: 'All', id: 'all' }
  ]
  return (
    <LargeModal
      handleLinkClick={() => setImportProjects(false)}
      handleSaveButton={handleImportClick}
      title="Import Projects"
      saveButtonText="Import"
      saveButtonDisabled={false}
    >
      <Container>
        <div>
          <Title>Procore Projects</Title>
          <Subtitle>Select the projects to import from Procore</Subtitle>
        </div>
        <div>
          <StyledTabs>
            {tabs.map(currentTab => (
              <StyledTabItem
                onClick={() => setTab(currentTab.id)}
                className={currentTab.id === tab ? 'active' : ''}
                key={currentTab.id}
              >
                {currentTab.label}
              </StyledTabItem>
            ))}
          </StyledTabs>
          <ProjectWrapper>
            {(tab === 'new' ? newProjects : procoreProjectsList).map((project: ProcoreProject) => {
              const isActive = selectedProjectIds.includes(project.id)
              return (
                <ProjectContainer
                  className={isActive ? 'active' : ''}
                  onClick={() => handleProjectClick(project.id)}
                  key={project.id}
                >
                  <ProjectText>{`${project.name} • ${project.project_number || ''}`}</ProjectText>
                  {isActive && <StyledTickIcon />}
                </ProjectContainer>
              )
            })}
          </ProjectWrapper>
          <ProjectsSelected>
            {selectedProjectIds.length || '#'} project{selectedProjectIds.length !== 1 && 's'}{' '}
            selected
          </ProjectsSelected>
        </div>
      </Container>
      {modalVisible && (
        <SmBannerModal
          heading={!procoreLoading && error ? 'Import Projects - Error' : 'Import Projects'}
          text={
            procoreLoading
              ? 'Connecting to Procore…'
              : !error
              ? `Successfully imported ${
                  selectedProjectIds.length
                } project${selectedProjectIds.length > 1 && 's'}`
              : 'Error importing procore projects'
          }
          icon={!procoreLoading && error ? <SyncProblemIcon /> : <SyncIcon />}
          linkText={procoreLoading ? 'Cancel' : undefined}
          onLinkClick={() => setModalVisible(false)}
          buttonText={!procoreLoading ? 'Ok' : undefined}
          onButtonClick={() => {
            setModalVisible(false)
            setImportProjects(false)
          }}
        />
      )}
    </LargeModal>
  )
}

export default ImportProjects
